/**
 * Карточка платежа в мини-аппе: «заплатил, а подписки нет». Номер у кассы и наш id
 * копируются, видно, что покупали и что выдано. «Спросить кассу» проводит ответ тем
 * же ядром, что callback: оплату зачисляет, покупку выдаёт, клиента уведомляет.
 */
import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { CheckCheck, ChevronRight, ExternalLink, Loader2, RadioTower } from 'lucide-react';
import { api, apiErrorMessage } from '@/lib/api';
import type { OwnerPaymentCard, PaymentCheckOutcome } from '@/api/types';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { BottomSheet } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { CopyValue } from '@/components/CopyValue';
import { getWebApp, haptic } from '@/lib/telegram';
import { useTgBack } from '@/lib/use-back';
import {
  TONE_CLS,
  auditLabel,
  fmtDateTime,
  ledgerLabel,
  money,
  paymentPurpose,
  paymentStatus,
  providerName,
  type Tone,
} from '@/lib/labels';

export const Route = createFileRoute('/payments/$id')({ component: PaymentCardPage });

/** Что ответила касса, словами и цветом. Исход считает бэк (`owner_payments.check_payment`). */
function outcomeText(outcome: PaymentCheckOutcome, detail: string | null): { text: string; tone: Tone } {
  switch (outcome) {
    case 'paid_now':
      return { text: 'Касса подтвердила оплату. Зачислили, выдали покупку, клиент получил уведомление.', tone: 'green' };
    case 'already_paid':
      return { text: 'Платёж уже был зачислен раньше, повторно ничего не начислено.', tone: 'green' };
    case 'closed_not_revived':
      return {
        text: 'Касса говорит «оплачено», а у нас счёт уже закрыт. Сам он не зачислится: сверьте сумму в кабинете кассы и пополните баланс клиента вручную.',
        tone: 'red',
      };
    case 'not_applied':
      return {
        text: 'Касса говорит «оплачено», но зачисление не прошло, скорее всего не сошлась сумма. Нужен разбор по логам.',
        tone: 'red',
      };
    case 'provider_pending':
      return { text: 'Касса ещё ждёт оплату или не знает этот счёт.', tone: 'amber' };
    case 'provider_closed':
      return { text: `Касса закрыла счёт${detail ? ` (${detail})` : ''}. У нас он закрыт тоже.`, tone: 'dim' };
    case 'provider_error':
      return { text: `Касса не ответила${detail ? `: ${detail}` : ''}. Попробуйте через минуту.`, tone: 'amber' };
  }
}

function PaymentCardPage() {
  const { id } = Route.useParams();
  useTgBack();
  const card = useQuery({
    queryKey: ['owner-payment-card', id],
    queryFn: () => api.paymentCard(id),
  });
  if (card.isPending) {
    return (
      <div className="p-4 space-y-4 pb-32">
        <div className="px-2 pt-2 space-y-2">
          <Skeleton className="h-7 w-2/3" />
          <Skeleton className="h-4 w-1/2" />
        </div>
        <Skeleton className="h-24 w-full rounded-2xl" />
        <Skeleton className="h-48 w-full rounded-2xl" />
      </div>
    );
  }
  if (card.isError) {
    return (
      <div className="p-4 pt-10 text-center space-y-3">
        <p className="text-sm text-muted-foreground">Карточка не загрузилась</p>
        <Button variant="secondary" size="sm" onClick={() => card.refetch()}>
          Повторить
        </Button>
      </div>
    );
  }
  return <PaymentView p={card.data} />;
}

function Title({ children }: { children: React.ReactNode }) {
  return <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">{children}</h3>;
}

function PaymentView({ p }: { p: OwnerPaymentCard }) {
  const [checkOpen, setCheckOpen] = useState(false);
  const [resolveOpen, setResolveOpen] = useState(false);
  const [last, setLast] = useState<{ outcome: PaymentCheckOutcome; detail: string | null } | null>(null);
  const st = paymentStatus(p.status);
  const purpose = paymentPurpose(typeof p.intent.type === 'string' ? p.intent.type : null);
  const clientName = p.client
    ? p.client.tg_username
      ? `@${p.client.tg_username}`
      : (p.client.email ?? (p.client.tg_user_id != null ? `ID ${p.client.tg_user_id}` : 'клиент'))
    : null;
  const gross = p.gross_amount_minor ?? p.amount_minor;
  const outcome = last ? outcomeText(last.outcome, last.detail) : null;

  return (
    <div className="p-4 space-y-4 pb-32">
      <div className="px-2 pt-2 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-xl font-bold tracking-tight">{money(gross, p.currency)}</h2>
          <span className={`whitespace-nowrap text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${TONE_CLS[st.tone]}`}>
            {st.label}
          </span>
        </div>
        <p className="mt-1 text-xs text-muted-foreground [overflow-wrap:anywhere]">
          {providerName(p.provider)} · {purpose}
          {p.client && clientName && (
            <>
              {' · '}
              <Link to="/clients/$id" params={{ id: p.client.id }} className="text-primary">
                {clientName}
              </Link>
            </>
          )}
          {p.bot_username && ` · @${p.bot_username}`}
          {' · '}
          <Link to="/partners/$id" params={{ id: p.partner_id }} className="text-primary">
            {p.partner_name ?? 'партнёр'}
          </Link>
        </p>
      </div>

      <Card>
        <CardContent className="p-5 space-y-3">
          <div className="text-sm">
            <div className="font-semibold">Спросить кассу</div>
            <div className="text-xs text-muted-foreground">
              {p.can_check
                ? 'Если касса подтвердит оплату, деньги зачислятся и покупка выдастся так же, как по callback.'
                : 'У этой кассы нет запроса статуса: проверка только в её кабинете.'}
            </div>
          </div>
          <Button className="w-full" disabled={!p.can_check} onClick={() => setCheckOpen(true)}>
            <RadioTower /> Спросить кассу
          </Button>
          {outcome && (
            <div role="status" className={`rounded-xl px-3 py-2 text-sm ${TONE_CLS[outcome.tone]}`}>
              {outcome.text}
            </div>
          )}
        </CardContent>
      </Card>

      {p.unfulfilled ? (
        <Card>
          <CardContent className="p-5 space-y-3">
            <div className="text-sm">
              <div className="font-semibold text-amber-500">Оплачен, а покупка не выдана</div>
              <div className="text-xs text-muted-foreground">
                Деньги клиенту зачислены. Если разобрались руками, отметьте платёж: он уйдёт из
                «Что горит».
              </div>
            </div>
            <Button variant="secondary" className="w-full" onClick={() => setResolveOpen(true)}>
              <CheckCheck /> Разобрано
            </Button>
          </CardContent>
        </Card>
      ) : (
        p.resolved_reason && (
          <p className="px-2 text-xs text-muted-foreground [overflow-wrap:anywhere]">
            Покупка не выдавалась: {p.resolved_reason}
            {p.resolved_by && ` · ${p.resolved_by}`}
          </p>
        )
      )}

      <Card>
        <CardContent className="p-5">
          <Title>Счёт</Title>
          <dl className="grid grid-cols-[auto_minmax(0,1fr)] items-center gap-x-3 gap-y-1.5 text-sm">
            <dt className="text-xs text-muted-foreground">У кассы</dt>
            <dd className="min-w-0">
              <CopyValue value={p.provider_invoice_id} title="номер у кассы" />
            </dd>
            <dt className="text-xs text-muted-foreground">Наш id</dt>
            <dd className="min-w-0">
              <CopyValue value={p.id} title="id платежа" />
            </dd>
            <dt className="text-xs text-muted-foreground">Выставлено</dt>
            <dd className="tabular-nums">{money(gross, p.currency)}</dd>
            {p.gross_amount_minor != null && p.gross_amount_minor !== p.amount_minor && (
              <>
                <dt className="text-xs text-muted-foreground">Зачисляется</dt>
                <dd className="tabular-nums">
                  {money(p.amount_minor, p.currency)}
                  <span className="text-muted-foreground"> · сбор {money(gross - p.amount_minor, p.currency)}</span>
                </dd>
              </>
            )}
            <dt className="text-xs text-muted-foreground">В долларах</dt>
            <dd className="tabular-nums">{money(p.amount_usd_minor, 'USD')}</dd>
            <dt className="text-xs text-muted-foreground">Создан</dt>
            <dd>{fmtDateTime(p.created_at)}</dd>
            <dt className="text-xs text-muted-foreground">Оплачен</dt>
            <dd>{p.paid_at ? fmtDateTime(p.paid_at) : p.status === 'paid' ? 'время не записано' : '—'}</dd>
            {p.pay_url && (
              <>
                <dt className="text-xs text-muted-foreground">Оплата</dt>
                <dd className="flex min-w-0 items-center gap-2">
                  <CopyValue value={p.pay_url} title="ссылка на оплату" />
                  <button
                    type="button"
                    onClick={() => {
                      const tg = getWebApp();
                      if (tg) tg.openLink(p.pay_url as string);
                      else window.open(p.pay_url as string, '_blank', 'noopener');
                    }}
                    className="flex h-8 w-8 shrink-0 items-center justify-center text-muted-foreground"
                    aria-label="Открыть ссылку на оплату"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </button>
                </dd>
              </>
            )}
          </dl>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-5">
          <Title>Что выдано</Title>
          {!p.subscriptions.length ? (
            <p className="text-sm text-muted-foreground">
              {p.status === 'paid'
                ? purpose === 'пополнение'
                  ? 'Деньги ушли на баланс клиента, подписки по этому платежу нет.'
                  : 'Подписки по платежу не нашлось. Если клиент ждёт покупку, её мог не выдать поставщик: проверьте карточку клиента.'
                : 'Пока ничего: платёж не оплачен.'}
            </p>
          ) : (
            <div className="divide-y divide-white/5">
              {p.subscriptions.map((s) => (
                <Link key={s.id} to="/subscriptions/$id" params={{ id: s.id }} className="flex min-h-11 items-center gap-2 py-2 active:bg-white/5">
                  <span className="min-w-0 flex-1 truncate text-sm">{s.plan_name}</span>
                  <span className={`whitespace-nowrap text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${TONE_CLS[s.is_active ? 'green' : 'dim']}`}>
                    {s.is_active ? 'активна' : 'истекла'}
                  </span>
                  <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-5">
          <Title>События</Title>
          {!p.events.length ? (
            <p className="text-sm text-muted-foreground">Проводок по платежу нет: деньги не зачислялись.</p>
          ) : (
            <ol className="space-y-2">
              {p.events.map((e, i) => (
                <li key={`${e.at}-${i}`} className="flex gap-3 text-sm">
                  <span className="w-24 shrink-0 text-[11px] text-muted-foreground tabular-nums">{fmtDateTime(e.at)}</span>
                  <span className="min-w-0 [overflow-wrap:anywhere]">
                    {e.source === 'audit' ? auditLabel(e.code) : ledgerLabel(e.code)}
                    {e.actor && <span className="text-muted-foreground"> · {e.actor}</span>}
                    {e.source === 'audit' && e.description && (
                      <span className="block text-xs text-muted-foreground">{e.description}</span>
                    )}
                  </span>
                </li>
              ))}
            </ol>
          )}
        </CardContent>
      </Card>

      <CheckSheet p={p} open={checkOpen} onClose={() => setCheckOpen(false)} onResult={setLast} />
      <ResolveSheet p={p} open={resolveOpen} onClose={() => setResolveOpen(false)} />
    </div>
  );
}

function CheckSheet({
  p,
  open,
  onClose,
  onResult,
}: {
  p: OwnerPaymentCard;
  open: boolean;
  onClose: () => void;
  onResult: (r: { outcome: PaymentCheckOutcome; detail: string | null }) => void;
}) {
  const [reason, setReason] = useState('');
  const qc = useQueryClient();
  const mut = useMutation({
    mutationFn: () => api.checkPayment(p.id, reason.trim()),
    onSuccess: (r) => {
      haptic(r.outcome === 'paid_now' || r.outcome === 'already_paid' ? 'success' : 'warning');
      onResult({ outcome: r.outcome, detail: r.detail });
      if (r.outcome === 'paid_now') toast.success('Оплата зачислена');
      void qc.invalidateQueries({ queryKey: ['owner-payment-card', p.id] });
      void qc.invalidateQueries({ queryKey: ['client'] });
      setReason('');
      onClose();
    },
    onError: (e) => {
      haptic('error');
      toast.error(apiErrorMessage(e) ?? 'Не получилось спросить кассу');
    },
  });
  return (
    <BottomSheet open={open} onClose={onClose} title="Спросить кассу">
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Если касса подтвердит оплату, деньги зачислятся сразу, покупка выдастся, клиенту уйдёт
          уведомление. Повторный запрос ничего не начислит второй раз.
        </p>
        {p.age_hours > 24 && p.status !== 'paid' && (
          <p className="rounded-xl bg-amber-500/10 px-3 py-2 text-sm text-amber-500">
            Счёту больше суток: автоматика такие не трогает. Убедитесь, что клиент действительно ждёт покупку.
          </p>
        )}
        <input
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          maxLength={200}
          placeholder="Причина: прислал скрин оплаты"
          className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-base outline-none focus:border-primary/60"
        />
        <Button className="w-full" disabled={mut.isPending || reason.trim().length < 2} onClick={() => mut.mutate()}>
          {mut.isPending ? <Loader2 className="animate-spin" /> : 'Спросить'}
        </Button>
      </div>
    </BottomSheet>
  );
}

function ResolveSheet({ p, open, onClose }: { p: OwnerPaymentCard; open: boolean; onClose: () => void }) {
  const [reason, setReason] = useState('');
  const qc = useQueryClient();
  const mut = useMutation({
    mutationFn: () => api.resolvePayment(p.id, reason.trim()),
    onSuccess: (card) => {
      haptic('success');
      toast.success('Платёж отмечен разобранным');
      qc.setQueryData(['owner-payment-card', p.id], card);
      setReason('');
      onClose();
    },
    onError: (e) => {
      haptic('error');
      toast.error(apiErrorMessage(e) ?? 'Не получилось отметить');
    },
  });
  return (
    <BottomSheet open={open} onClose={onClose} title="Отметить разобранным">
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Подписку это не выдаёт и деньги не двигает. Платёж перестанет висеть в «Что горит».
        </p>
        <input
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          maxLength={200}
          placeholder="Что сделали: деньги на балансе"
          className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-base outline-none focus:border-primary/60"
        />
        <Button className="w-full" disabled={mut.isPending || reason.trim().length < 2} onClick={() => mut.mutate()}>
          {mut.isPending ? <Loader2 className="animate-spin" /> : 'Отметить'}
        </Button>
      </div>
    </BottomSheet>
  );
}
